/**
 * HomeDocCard — Individual document card for the "Your Documents" section.
 *
 * Renders a complete card matching the design spec exactly:
 * 1. Card layout with proper styling (border, padding, shadow)
 * 2. Document type badge (CSR, PSUR, DSUR with correct colors)
 * 3. State dot with animation + state label
 * 4. Document title and subtitle/metadata
 * 5. Progress bar with dynamic width (%, color by state)
 * 6. Progress information ("X of Y sections" + percentage)
 * 7. Tags: Gaps badge (if > 0), Notes badge (if > 0)
 * 8. Avatar with user initials (if owner data exists)
 * 9. "web studio ↗" link for non-current documents
 * 10. "THIS DOCUMENT" badge for currently-bound document
 *
 * Every element is present and styled per spec §4.3.
 */

import { makeStyles } from "@fluentui/react-components";
import * as React from "react";
import {
  brandColors,
  doctypeChipColorFor,
  inkColors,
  surfaceColors,
  fonts,
  statusColors,
} from "../tokens";
import type { HomeDoc } from "../homeSceneMapping";
import { formatTokens } from "../utils/formatTokens";
import { HomeStateDot } from "./HomeStateDot";

/** Compact "created" date, e.g. "Sep 2, 2026". Empty when missing/invalid. */
function formatCreatedDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

interface HomeDocCardProps {
  doc: HomeDoc;
  onLoad: (doc: HomeDoc) => void;
  loading?: boolean;
  /** Drafts-tab affordance: open the run's canonical .docx in a new Word
   *  window. When provided (together with the ⋯ menu), a "Open in Word" item
   *  is shown. Undefined on the empty-doc Home, where the card is Load-only. */
  onOpenExternal?: (doc: HomeDoc) => void;
  /** Drafts-tab affordance: download the run's audit bundle (.zip). Renders a
   *  "Download bundle" menu item when provided. */
  onDownloadBundle?: (doc: HomeDoc) => void;
  /** Which overflow action is mid-flight, so its menu item can show progress
   *  copy ("Opening…" / "Zipping…"). Null when idle. */
  menuBusy?: "open" | "bundle" | null;
  /** When provided, the ⋯ menu carries a "Rename…" item that edits the draft's
   *  display name inline. Called with the new name on save. */
  onRename?: (doc: HomeDoc, name: string) => void | Promise<void>;
  /** When provided, the ⋯ menu carries a "Move to folder…" item. The scene
   *  owns the folder chooser; the card only asks. */
  onMoveToFolder?: (doc: HomeDoc) => void;
}

const useStyles = makeStyles({
  // ─────────────────────────────────────────────────────────────
  // CARD CONTAINER
  // ─────────────────────────────────────────────────────────────
  card: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "12px 13px",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(76,51,138,0.04)",
    position: "relative",
    transition: "background-color 0.2s ease",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "#fafafa",
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },

  cardCurrent: {
    border: `1.5px solid ${brandColors.mid}`,
  },

  cardOther: {
    border: `1px solid ${surfaceColors.line}`,
  },

  // ─────────────────────────────────────────────────────────────
  // HEADER ROW: [Doctype Badge] [Spacer] [State Dot + Label]
  // ─────────────────────────────────────────────────────────────
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
  },

  // DOCTYPE CHIP: CSR, PSUR, DSUR, CER with correct colors
  doctypeChip: {
    fontFamily: fonts.body,
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    padding: "2px 7px",
    borderRadius: "3px",
    whiteSpace: "nowrap",
  },

  spacer: {
    flex: 1,
  },

  // STATE DOT + LABEL ROW
  stateRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  stateLabel: {
    fontFamily: fonts.body,
    fontSize: "10.5px",
    color: inkColors.ink3,
  },

  // ─────────────────────────────────────────────────────────────
  // DOCUMENT TITLE & SUBTITLE
  // ─────────────────────────────────────────────────────────────
  titleRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  title: {
    fontFamily: fonts.display,
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
    marginTop: "9px",
    marginBottom: 0,
  },

  subtitle: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink4,
    marginTop: "1px",
    marginBottom: 0,
  },

  // Created date + token totals line, below the subtitle.
  metaLine: {
    fontFamily: fonts.body,
    fontSize: "10px",
    color: inkColors.ink4,
    marginTop: "3px",
  },

  // Title + inline rename controls.
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginTop: "9px",
  },
  renamePencil: {
    border: "none",
    background: "transparent",
    color: inkColors.ink4,
    cursor: "pointer",
    fontSize: "11px",
    lineHeight: 1,
    padding: "2px",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: surfaceColors.paperDim,
      color: inkColors.ink2,
    },
  },
  renameInput: {
    fontFamily: fonts.display,
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    border: `1px solid ${brandColors.mid}`,
    borderRadius: "4px",
    padding: "2px 6px",
    marginTop: "9px",
    width: "100%",
    boxSizing: "border-box",
  },

  // ─────────────────────────────────────────────────────────────
  // PROGRESS BAR SECTION
  // ─────────────────────────────────────────────────────────────
  progressSection: {
    marginTop: "10px",
  },

  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: fonts.body,
    fontSize: "9.5px",
    color: inkColors.ink4,
    marginBottom: "4px",
  },

  // Progress track (background bar)
  progressTrack: {
    height: "4px",
    borderRadius: "2px",
    backgroundColor: "#F0EBF7",
    overflow: "hidden",
  },

  progressFill: {
    height: "100%",
    borderRadius: "2px",
  },

  // ─────────────────────────────────────────────────────────────
  // FOOTER ROW: [Gaps Badge] [Notes Badge] [Spacer] [Web Link] [Avatar]
  // ─────────────────────────────────────────────────────────────
  footerRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "10px",
  },

  // BADGE STYLING: Gaps & Notes pills
  badge: {
    fontFamily: fonts.body,
    fontSize: "9.5px",
    fontWeight: 600,
    padding: "1px 7px",
    borderRadius: "10px",
  },

  // "web studio ↗" link for non-current docs
  webStudioLink: {
    fontFamily: fonts.body,
    fontSize: "9.5px",
    color: inkColors.ink4,
  },

  // "Reconnect →" / "Join live →" action for in-progress runs. Brand-weighted
  // so the reconnect path reads as the primary action on a live card.
  joinLiveLink: {
    fontFamily: fonts.body,
    fontSize: "10px",
    fontWeight: 700,
    color: brandColors.primary,
    letterSpacing: "0.2px",
  },

  // AVATAR: Colored circle with user initials
  avatar: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: fonts.body,
    fontSize: "8.5px",
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },

  // ─────────────────────────────────────────────────────────────
  // OVERFLOW MENU (Drafts tab): ⋯ trigger + popover with Open / Bundle.
  // The card root is a role="button" div so this real <button> can live
  // inside it without nesting a button in a button.
  // ─────────────────────────────────────────────────────────────
  menuWrap: {
    position: "relative",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
  menuTrigger: {
    width: "22px",
    height: "22px",
    borderRadius: "5px",
    border: "none",
    background: "transparent",
    color: inkColors.ink4,
    fontSize: "15px",
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": {
      backgroundColor: surfaceColors.paperDim,
      color: inkColors.ink2,
    },
  },
  menuBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "default",
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 41,
    minWidth: "150px",
    backgroundColor: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "7px",
    boxShadow: "0 6px 20px rgba(76,51,138,0.16)",
    padding: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  menuItem: {
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink2,
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: "5px",
    padding: "6px 9px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: surfaceColors.paperDim,
    },
    "&:disabled": {
      color: inkColors.ink5,
      cursor: "default",
    },
  },
});

export function HomeDocCard({
  doc,
  onLoad,
  loading,
  onOpenExternal,
  onDownloadBundle,
  menuBusy = null,
  onRename,
  onMoveToFolder,
}: HomeDocCardProps): React.ReactElement {
  const styles = useStyles();
  const chipColor = doctypeChipColorFor(doc.doctype);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const hasMenu = Boolean(onOpenExternal || onDownloadBundle || onMoveToFolder || onRename);

  // Inline rename state. Editing swaps the title for a text input; Enter saves
  // via onRename, Escape cancels. Kept local — the parent re-fetches to repaint.
  const [editing, setEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(doc.title);
  const beginRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftName(doc.title);
    setEditing(true);
  };
  const commitRename = () => {
    setEditing(false);
    const next = draftName.trim();
    if (onRename && next && next !== doc.title) {
      void onRename(doc, next);
    }
  };

  const createdLabel = formatCreatedDate(doc.createdAt);
  const hasTokens = doc.inputTokens > 0 || doc.outputTokens > 0;
  const tokenLabel = hasTokens
    ? `${formatTokens(doc.inputTokens)} in · ${formatTokens(doc.outputTokens)} out`
    : "";
  const metaLine = [createdLabel, tokenLabel].filter(Boolean).join("  ·  ");

  // Progress bar color by state:
  // - drafting (active) → gradient (brand → mid purple)
  // - review (awaiting approval) → solid cool green (#26DF9B)
  // - indexing (processing) → solid warn orange (#F5A524)
  // - done (completed) → solid cool green (#26DF9B)
  const progressFillColor = (() => {
    switch (doc.state) {
      case "review":
        return statusColors.cool; // #26DF9B (green)
      case "indexing":
        return statusColors.warn; // #F5A524 (orange)
      case "done":
        return statusColors.cool; // #26DF9B (green)
      default: // drafting
        return `linear-gradient(90deg, ${brandColors.primary}, ${brandColors.mid})`; // purple gradient
    }
  })();

  const handleClick = () => {
    if (!loading) {
      onLoad(doc);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only the card itself activates Load. Keydowns bubbling up from the nested
    // ⋯ trigger / menu items must NOT also fire Load.
    if (e.target !== e.currentTarget) return;
    if ((e.key === "Enter" || e.key === " ") && !loading) {
      e.preventDefault();
      onLoad(doc);
    }
  };

  // Menu-item handlers stop propagation so opening/bundling never also fires
  // the card's Load click.
  const runMenuAction = (action: (doc: HomeDoc) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    action(doc);
  };

  return (
    <div
      className={`${styles.card} ${doc.current ? styles.cardCurrent : styles.cardOther}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Load ${doc.title}`}
    >
      {/* ═════════════════════════════════════════════════════════════ */}
      {/* 2. HEADER ROW: [Doctype Badge] [Spacer] [State Dot + Label] */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <div className={styles.headerRow}>
        {/* Doctype chip (CSR, PSUR, DSUR, CER with colors) */}
        <div
          className={styles.doctypeChip}
          style={{
            backgroundColor: chipColor.bg,
            color: chipColor.fg,
          }}
        >
          {doc.doctype}
        </div>

        {/* Spacer to push state indicator to right */}
        <div className={styles.spacer} />

        {/* State indicator: animated dot + state label */}
        <div className={styles.stateRow}>
          <HomeStateDot state={doc.state} />
          <span className={styles.stateLabel}>{doc.stateLabel}</span>
        </div>

        {/* Overflow menu (Drafts tab only): Open in Word / Download bundle.
            A real <button> nested legally because the card root is a
            role="button" div, not a <button>. */}
        {hasMenu && (
          <div className={styles.menuWrap}>
            <button
              type="button"
              className={styles.menuTrigger}
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
            >
              ⋯
            </button>
            {menuOpen && (
              <>
                {/* Outside-click catcher. stopPropagation so the card's Load
                    click never fires when dismissing the menu. */}
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  className={styles.menuBackdrop}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                  }}
                />
                <div className={styles.menu} role="menu">
                  {onRename && (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      onClick={(e) => {
                        setMenuOpen(false);
                        beginRename(e);
                      }}
                    >
                      Rename…
                    </button>
                  )}
                  {onOpenExternal && (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      disabled={menuBusy !== null}
                      onClick={runMenuAction(onOpenExternal)}
                    >
                      {menuBusy === "open" ? "Opening…" : "Open in Word"}
                    </button>
                  )}
                  {onDownloadBundle && (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      disabled={menuBusy !== null}
                      onClick={runMenuAction(onDownloadBundle)}
                    >
                      {menuBusy === "bundle" ? "Zipping…" : "Download bundle"}
                    </button>
                  )}
                  {onMoveToFolder && (
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.menuItem}
                      onClick={runMenuAction(onMoveToFolder)}
                    >
                      Move to folder…
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ═════════════════════════════════════════════════════════════ */}
      {/* 3. TITLE & SUBTITLE */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <div className={styles.titleRow}>
        {editing ? (
          <input
            className={styles.renameInput}
            value={draftName}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            aria-label="Draft name"
          />
        ) : (
          <div className={styles.titleWrap}>
            <h3 className={styles.title} style={{ marginTop: 0 }}>
              {doc.title}
            </h3>
          </div>
        )}
        <p className={styles.subtitle}>{doc.sub}</p>
        {metaLine && <p className={styles.metaLine}>{metaLine}</p>}
      </div>


      {/* ═════════════════════════════════════════════════════════════ */}
      {/* 4. PROGRESS BAR */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <div className={styles.progressSection}>
        {/* Progress label: "X of Y sections" + percentage */}
        <div className={styles.progressLabel}>
          <span>{doc.progressLabel}</span>
          <span>{doc.pct}%</span>
        </div>

        {/* Progress bar track with dynamic fill */}
        <div className={styles.progressTrack}>
          <div
            style={{
              display: "block",
              height: "100%",
              borderRadius: "2px",
              width: `${doc.pct}%`,
              background: progressFillColor,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════ */}
      {/* 5. FOOTER ROW: [Gaps Badge] [Notes Badge] [Spacer] [Web Link] [Avatar] */}
      {/* ═════════════════════════════════════════════════════════════ */}
      <div className={styles.footerRow}>
        {/* Gaps badge - shown only if gaps > 0 */}
        {doc.gaps > 0 && (
          <div
            className={styles.badge}
            style={{
              backgroundColor: "#FFFBEB",
              border: "1px solid #FCD34D",
              color: "#92400E",
            }}
          >
            {doc.gaps} gaps
          </div>
        )}

        {/* Notes badge - shown only if notes > 0 */}
        {doc.notes > 0 && (
          <div
            className={styles.badge}
            style={{
              backgroundColor: "#F3EDFC",
              color: brandColors.primary,
            }}
          >
            {doc.notes} notes
          </div>
        )}

        {/* Spacer to align avatar and web link to right */}
        <div className={styles.spacer} />

        {/* Primary action. A live run reconnects (join-live) — labelled
            "Reconnect →" when it's the doc currently open, else "Join live →".
            A completed run on another doc offers the web-studio deep link. */}
        {doc.live ? (
          <span className={styles.joinLiveLink}>{doc.current ? "Reconnect →" : "Join live →"}</span>
        ) : (
          !doc.current && <span className={styles.webStudioLink}>web studio ↗</span>
        )}

        {/* Avatar with user initials - shown only if owner exists */}
        {doc.owner && (
          <div
            className={styles.avatar}
            style={{
              backgroundColor: doc.ownerBg || brandColors.mid,
            }}
          >
            {doc.owner}
          </div>
        )}
      </div>
    </div>
  );
}
